import { z } from 'zod/v4';

import { CurrencyRuntype } from '../backendTypes';
import { FeatureFlagNameRuntype } from '../featureFlagNameBackendTypes';
import { PreferenceStatusEnumRuntype } from '../part';
import { AssemblySettingsRuntype } from './assemblySettingsBackendTypes';
import { ComponentImportSettingsRuntype } from './componentSettingsBackendTypes';
import { SupplierPortalSettingsRuntype } from './supplierPortalSettingsBackendTypes';

export { StepEnum, StepEnumRuntype } from './supplierPortalSettingsBackendTypes';

const BackendExchangeRatesDTORuntype = z.null().or(z.record(z.string(), z.string()));

export interface GlobalCurrencySettingsDTO extends z.infer<typeof GlobalCurrencySettingsDTORuntype> {}
export const GlobalCurrencySettingsDTORuntype = z.object({
    currency: CurrencyRuntype,
    exchange_rates: BackendExchangeRatesDTORuntype,
});

export interface PublicExchangeRateSummaryDTO extends z.infer<typeof PublicExchangeRateSummaryDTORuntype> {}
export const PublicExchangeRateSummaryDTORuntype = z.object({
    last_updated: z.string().nullable(),
    base_currency: z.string(),
    rates: z.object({
        base: z.string(),
        rates: z.record(CurrencyRuntype, z.string()),
    }),
});

export const ManualRfqNumberRuntype = z.object({
    type: z.literal('Manual'),
});

export const AutomaticRfqNumberDataRuntype = z.object({
    start: z.number(),
    end: z.number(),
    current: z.number(),
    prefix: z.string().nullable(),
});
export const AutomaticRfqNumberRuntype = z.object({
    type: z.literal('Automatic'),
    data: AutomaticRfqNumberDataRuntype,
});
export const PatchAutomaticRfqNumberRuntype = z.object({
    type: z.literal('Automatic'),
    data: AutomaticRfqNumberDataRuntype.omit({ current: true }),
});

export const AutomaticRfqNumberSettingsRuntype = ManualRfqNumberRuntype.or(AutomaticRfqNumberRuntype);
export const PatchAutomaticRfqNumberSettingsRuntype = ManualRfqNumberRuntype.or(PatchAutomaticRfqNumberRuntype);

export type PatchAutomaticRfqNumberSettings = z.infer<typeof PatchAutomaticRfqNumberSettingsRuntype>;

export enum WarningStatusEnum {
    warning = 'warning',
    noWarning = 'no_warning',
}

export const WarningStatusRuntype = z.enum(WarningStatusEnum);

export interface BomWarningsConfiguration extends z.infer<typeof BomWarningsConfigurationRuntype> {}

export const BomWarningsConfigurationRuntype = z.object({
    availability: WarningStatusRuntype,
    lifecycle: WarningStatusRuntype,
    compliance: WarningStatusRuntype,
    part_data: WarningStatusRuntype,
});

export const SelfSignupColorsRuntype = z.object({
    primary_color: z.string(),
    primary_dark_color: z.string(),
    background_color: z.string(),
});

export const SelfSignupSettingsRuntype = z.object({
    is_self_signup_enabled: z.boolean(),
    is_customer_user_management_enabled: z.boolean(),
    are_private_customers_allowed: z.boolean(),
    default_contact_person: z.string().nullable(),
    organization_logo: z.string().nullable(),
    colors: SelfSignupColorsRuntype,
});

export type SelfSignupSettingsDTO = z.infer<typeof SelfSignupSettingsRuntype>;

export const PatchSelfSignupSettingsRuntype = z.object({
    default_contact_person: z.string().nullable().optional(),
    is_self_signup_enabled: z.boolean().optional(),
    are_private_customers_allowed: z.boolean().optional(),
    is_customer_user_management_enabled: z.boolean().optional(),
    organization_logo: z.string().nullable().optional(),
    colors: SelfSignupColorsRuntype.optional(),
});

export const ManufacturerPreferenceInputRuntype = z.object({
    part_category_id: z.number(),
    manufacturers: z.array(z.string()),
    preference_status: PreferenceStatusEnumRuntype,
});

export const ManufacturerPreferenceUpdateRuntype = z.object({
    manufacturers: z.array(z.string()),
    preference_status: PreferenceStatusEnumRuntype,
});

export const ManufacturerPreferenceDTORuntype = z.object({
    part_category: z.object({
        id: z.number(),
        name: z.string(),
    }),
    manufacturers: z.array(
        z.object({
            id: z.string(),
            name: z.string(),
        }),
    ),
    preference_status: PreferenceStatusEnumRuntype,
});

export const ProductCostingAccessEnum = z.enum(['Full', 'Lite', 'CalculationOnly']);

export type ProductCostingAccess = z.infer<typeof ProductCostingAccessEnum>;

export const FeatureFlagRequestRuntype = z.object({
    flag: FeatureFlagNameRuntype,
    enabled: z.boolean(),
});
export type FeatureFlagRequestDTO = z.infer<typeof FeatureFlagRequestRuntype>;

export const FeatureFlagConfigRuntype = z.object({
    feature_flag: FeatureFlagNameRuntype,
    tenant_enabled: z.boolean(),
    always_enabled_users: z.array(z.string()),
    always_disabled_users: z.array(z.string()),
});
export type FeatureFlagConfigDTO = z.infer<typeof FeatureFlagConfigRuntype>;

export const FeatureFlagConfigsResponseRuntype = z.object({
    data: z.array(FeatureFlagConfigRuntype),
});
export type FeatureFlagConfigsResponse = z.infer<typeof FeatureFlagConfigsResponseRuntype>;

export const FeatureFlagUserOverridesRequestRuntype = z.object({
    flag: FeatureFlagNameRuntype,
    always_enabled_users: z.array(z.string()),
    always_disabled_users: z.array(z.string()),
});
export type FeatureFlagUserOverridesRequestDTO = z.infer<typeof FeatureFlagUserOverridesRequestRuntype>;

export const ImageAssetsRuntype = z.object({
    navbar_dark: z.string(),
    navbar_light: z.string(),
    favicon: z.string(),
});

export const OrganizationSettingsDTORuntype = z.object({
    image_assets: ImageAssetsRuntype,
    auto_rfq_number_settings: AutomaticRfqNumberSettingsRuntype,
    supplier_portal_settings: SupplierPortalSettingsRuntype,
    assembly_settings: AssemblySettingsRuntype,
    bom_warnings_configuration: BomWarningsConfigurationRuntype,
    component_import_settings: ComponentImportSettingsRuntype,
});
export interface OrganizationSettingsDTO extends z.infer<typeof OrganizationSettingsDTORuntype> {}

export const PatchOrganizationSettingsDTORuntype = z.object({
    image_assets: ImageAssetsRuntype.optional(),
    auto_rfq_number_settings: AutomaticRfqNumberSettingsRuntype.optional(),
    supplier_portal_settings: SupplierPortalSettingsRuntype.optional(),
    assembly_settings: AssemblySettingsRuntype.optional(),
    bom_warnings_configuration: BomWarningsConfigurationRuntype.optional(),
    component_import_settings: ComponentImportSettingsRuntype.optional(),
});
export type PatchOrganizationSettingsDTO = z.infer<typeof PatchOrganizationSettingsDTORuntype>;
